import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import type {
    PenRef,
    TestPenReason,
    TestPenResult,
    TestResult,
    TestScadaResult,
    TestStatus,
} from '@/domain/providerConfig';
import { parseLocale } from '@/utils/localeFormatters';

import { StatusPill, type StatusTone } from './StatusPill';

import styles from './TestResultPanel.module.scss';

export interface TestResultPanelProps {
    result: TestResult | null;
    /** The connection changed since this result, so it is shown with a prompt to test again. */
    stale?: boolean;
    pens: PenRef[];
}

/**
 * A pen's message is the provider's own text and can name a token URL host or path, so it
 * is never shown. Each row gives one piece of advice: a cause the server names replaces the
 * status (a timeout, say, rather than the unreachable status's advice to check the
 * address), else the status says what to check, and only an error with no named cause,
 * whose status says nothing to check, adds the generic advice.
 */
const REASON_KEY: Readonly<Record<TestPenReason, string>> = {
    stored_credential_undecryptable: 'providerConfig.test.message.storedCredentialUnusable',
    timed_out: 'providerConfig.test.message.timedOut',
    scada_item_unknown: 'providerConfig.test.message.scadaItemUnknown',
};
const OTHER_MESSAGE = 'providerConfig.test.message.other';

const STATUS_TONE: Readonly<Record<TestStatus, StatusTone>> = {
    ok: 'good',
    no_data: 'warn',
    config_incomplete: 'warn',
    auth_failed: 'bad',
    unreachable: 'bad',
    error: 'bad',
};

function statusKey(pen: TestPenResult): string {
    if (pen.reason !== null) return REASON_KEY[pen.reason];
    // A SCADA-only pen reads no daily data, so "data arrived" would say more than it checked.
    if (pen.status === 'ok' && pen.scada?.scadaOnly)
        return 'providerConfig.test.status.okScadaOnly';
    return `providerConfig.test.status.${pen.status}`;
}

/** What the SCADA lookup found; item ids are shown, since the form shows them too. */
function ScadaNotes({ scada }: { scada: TestScadaResult }) {
    const { t } = useTranslation();
    return (
        <>
            <span className={styles.note}>
                {t('providerConfig.test.scada', { found: scada.found, count: scada.itemCount })}
            </span>
            {scada.unknown.length > 0 && (
                <span className={styles.note}>
                    {t('providerConfig.test.scadaUnknown', { ids: scada.unknown.join(', ') })}
                </span>
            )}
            {scada.unverified > 0 && (
                <span className={styles.note}>
                    {t('providerConfig.test.scadaUnverified', { count: scada.unverified })}
                </span>
            )}
        </>
    );
}

function TestResultPanel({ result, stale = false, pens }: TestResultPanelProps) {
    const { t, i18n } = useTranslation();
    const nameOf = (cageId: number): string =>
        pens.find((pen) => pen.cageId === cageId)?.name ?? String(cageId);
    const list = new Intl.ListFormat(parseLocale(i18n.language), { type: 'conjunction' });

    function signalsText(pen: TestPenResult): string {
        return list.format(pen.signals.map((signal) => t(`providerConfig.subjects.${signal}`)));
    }

    return (
        // Always mounted: a live region added together with its text is not announced.
        <section className={styles.testResult} aria-live="polite">
            {result !== null && (
                <>
                    {stale && (
                        <Alert tone="warning" size="small" live="off">
                            {t('providerConfig.test.stale')}
                        </Alert>
                    )}
                    <p className={styles.note}>
                        {t('providerConfig.test.window', { from: result.from, to: result.to })}
                    </p>
                    <ul className={styles.testList}>
                        {result.results.map((pen) => {
                            return (
                                <li key={pen.cageId} className={styles.testRow}>
                                    <span className={styles.penName}>{nameOf(pen.cageId)}</span>
                                    <StatusPill
                                        tone={STATUS_TONE[pen.status]}
                                        label={t(statusKey(pen))}
                                        className={styles.status}
                                    />
                                    {!pen.scada?.scadaOnly && (
                                        <span className={styles.note}>
                                            {t('providerConfig.test.daysWithData', {
                                                count: pen.daysWithData,
                                            })}
                                        </span>
                                    )}
                                    {pen.signals.length > 0 && (
                                        <span className={styles.note}>
                                            {t('providerConfig.test.signals', {
                                                signals: signalsText(pen),
                                            })}
                                        </span>
                                    )}
                                    {pen.scada !== null && <ScadaNotes scada={pen.scada} />}
                                    {pen.status === 'error' && pen.reason === null && (
                                        <span className={styles.note}>{t(OTHER_MESSAGE)}</span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </section>
    );
}

export default TestResultPanel;
export { TestResultPanel };
